import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MotifModule } from "@ey-xd/motif-wc-angular";

@Component({
  selector: 'app-custom-motif-uploader',
  standalone: true,
  imports: [MotifModule, CommonModule],
  templateUrl: './custom-motif-uploader.component.html',
  styleUrl: './custom-motif-uploader.component.scss',
})
export class CustomMotifUploaderComponent implements OnInit {
  @Input() dropMessage: string = '<b>Click to upload</b> or drag and drop';
  @Input() acceptZip: boolean = false;
  @Input() acceptDoc: boolean = false;
  @Input() acceptExcel: boolean = false;

  allowedFormatsMessage: string = 'XLSX, XLSM, XLS, XLSB, or CSV (max. 20MB)';
  errorMessage: string = '';

  acceptedFormats: string = '.xlsx, .xlsm, .csv, .xls, .xlsb';

  MAX_FILE_SIZE: number = 20 * 1024 * 1024; // 20MB

  @Output() fileEmitter = new EventEmitter<File | any>();
  @Output() fileHasError = new EventEmitter<boolean>(false);

  @Input() file: File | any = undefined;
  @Input() reducedWidth: boolean = false;

  hasError: boolean = false;

  ngOnInit(): void {
    if (this.acceptZip) {
      this.allowedFormatsMessage = 'zip file (max. 20MB)';
      this.acceptedFormats = '.zip';
    }
    if (this.acceptDoc && this.acceptExcel) {
      this.allowedFormatsMessage =
        'XLSX, XLSM, XLS, XLSB or DOC, DOCX (max. 20MB)';
      this.acceptedFormats = '.xlsx, .xlsm, .xls, .xlsb, .doc, .docx';
    }
  }

  onFilesChange(event: any): void {
    const { action, added, rejected, files } = event.detail;

    if (action === 'add' && added && added.length > 0) {
      this.handleFile(added[0]);
    } else if (action === 'reject' && rejected && rejected.length > 0) {
      this.hasError = true;
      this.errorMessage = this.setErrorMessage();
      this.fileHasError.emit(this.hasError);
    } else if (action === 'clear') {
      this.resetUploader();
    }
  }

  onFileRemove(event: any): void {
    this.resetUploader();
  }

  private handleFile(file: File) {
    if (this.hasError) {
      this.hasError = false;
      this.errorMessage = '';
      this.fileHasError.emit(this.hasError);
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isValidType = this.getValidType(file, fileExtension);

    if (isValidType && file.size <= this.MAX_FILE_SIZE) {
      this.file = file;
      this.fileEmitter.emit(file);
    } else {
      this.hasError = true;
      this.errorMessage = this.setErrorMessage();
      this.fileHasError.emit(this.hasError);
    }
  }

  resetUploader(): void {
    this.file = undefined;
    this.fileEmitter.emit(this.file);
    this.hasError = false;
    this.errorMessage = '';
    this.fileHasError.emit(this.hasError);
  }

  private getValidType(file: File, fileExtension: string | any): boolean {
    if (this.acceptDoc && this.acceptExcel) {
      return (
        file.type ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        fileExtension === 'docx' ||
        file.type === 'application/msword' ||
        fileExtension === 'doc' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        fileExtension === 'xlsx' ||
        file.type === 'application/vnd.ms-excel.sheet.macroEnabled.12' ||
        fileExtension === 'xlsm' ||
        file.type === 'application/msexcel' ||
        fileExtension === 'xls' ||
        file.type === 'application/vnd.ms-excel.sheet.binary.macroEnabled.12' ||
        fileExtension === 'xlsb'
      );
    }

    if (this.acceptZip) {
      return file.type === 'application/zip' || fileExtension === 'zip';
    } else {
      return (
        file.type === 'text/csv' ||
        fileExtension === 'csv' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        fileExtension === 'xlsx' ||
        file.type === 'application/vnd.ms-excel.sheet.macroEnabled.12' ||
        fileExtension === 'xlsm' ||
        file.type === 'application/msexcel' ||
        fileExtension === 'xls' ||
        file.type === 'application/vnd.ms-excel.sheet.binary.macroEnabled.12' ||
        fileExtension === 'xlsb'
      );
    }
  }

  private setErrorMessage(): string {
    if (this.acceptDoc && this.acceptExcel) {
      return `Select a XLSX, XLSM, XLS, XLSB or DOC, DOCX file under ${
        this.MAX_FILE_SIZE / (1024 * 1024)
      }MB.`;
    }

    if (this.acceptZip) {
      return `Select a zip file under ${this.MAX_FILE_SIZE / (1024 * 1024)}MB.`;
    } else {
      return `Select an XLSX, XLSM, XLS, XLSB or CSV file under ${
        this.MAX_FILE_SIZE / (1024 * 1024)
      }MB.`;
    }
  }
}
